import { useEffect, useState } from 'react';
import { authAPI, prodigyGroupsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import { toast } from 'react-toastify';

const TYPE_COLORS = {
  pos: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'POS' },
  features: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Features' },
  relations: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Relaciones' },
  ner: { bg: 'bg-red-100', text: 'text-red-800', label: 'NER' },
};

const TypeBadge = ({ type }) => {
  const c = TYPE_COLORS[type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: type };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

const EMPTY_FORM = { name: '', annotation_type: 'pos', member_user_ids: [], initial_batch: 1, start_batch: 1, end_batch: '' };

const ProdigyAdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);

  // Instances monitor
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [stoppingInstance, setStoppingInstance] = useState({});

  // Create modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Inline edit states
  const [editingBatch, setEditingBatch] = useState({}); // {groupId: batchValue}
  const [editingConfig, setEditingConfig] = useState({}); // {key: value}
  const [editingName, setEditingName] = useState({}); // {groupId: name}

  // Add member inline
  const [addingMember, setAddingMember] = useState({}); // {groupId: userId}

  // Service control
  const [serviceStatus, setServiceStatus] = useState({}); // {groupId: 'starting'|'stopping'|null}

  // Batch range inline edit
  const [editingRange, setEditingRange] = useState({}); // {groupId: {start_batch, end_batch}}

  useEffect(() => {
    if (!user?.is_superuser) return;
    Promise.all([loadGroups(), loadUsers(), loadConfig()]).finally(() => setLoading(false));
  }, [user]);

  const loadInstances = async () => {
    try {
      const res = await prodigyGroupsAPI.getInstances();
      setInstances(res.data?.instances || []);
    } catch {
      // non-blocking
    }
  };

  useEffect(() => {
    if (!user?.is_superuser) return;
    if (activeTab !== 'processes') return;
    setLoadingInstances(true);
    loadInstances().finally(() => setLoadingInstances(false));
    const t = setInterval(loadInstances, 10000);
    return () => clearInterval(t);
  }, [activeTab, user]);

  const handleStopInstance = async (serviceId) => {
    setStoppingInstance(prev => ({ ...prev, [serviceId]: true }));
    try {
      await prodigyGroupsAPI.stopInstance(serviceId);
      toast.success(`Instancia ${serviceId} detenida`);
      await loadInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Error al detener ${serviceId}`);
    } finally {
      setStoppingInstance(prev => { const n = { ...prev }; delete n[serviceId]; return n; });
    }
  };

  const loadGroups = async () => {
    try {
      const res = await prodigyGroupsAPI.getGroups();
      setGroups(res.data || []);
    } catch {
      toast.error('Error al cargar grupos Prodigy');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await authAPI.getAllUsers();
      setUsers((res.data || []).filter(u => u.is_active));
    } catch {
      // non-blocking
    }
  };

  const loadConfig = async () => {
    try {
      const res = await prodigyGroupsAPI.getConfig();
      setConfig(res.data || []);
    } catch {
      toast.error('Error al cargar configuración Prodigy');
    }
  };

  // ── Create group ──────────────────────────────────────────────────────────

  const openModal = () => { setForm(EMPTY_FORM); setShowModal(true); };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      await prodigyGroupsAPI.createGroup({
        name: form.name.trim(),
        annotation_type: form.annotation_type,
        member_user_ids: form.member_user_ids.map(Number),
        initial_batch: Number(form.initial_batch),
        start_batch: Number(form.start_batch) || 1,
        end_batch: form.end_batch !== '' ? Number(form.end_batch) : null,
      });
      toast.success('Grupo creado');
      setShowModal(false);
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear grupo');
    } finally {
      setSaving(false);
    }
  };

  // ── Service control ───────────────────────────────────────────────────────

  const handleStartService = async (groupId) => {
    setServiceStatus(prev => ({ ...prev, [groupId]: 'starting' }));
    try {
      const resp = await prodigyGroupsAPI.startAllBatches(groupId);
      const { started = [], skipped = [], failed = [] } = resp.data || {};
      const msg = `${started.length} iniciadas, ${skipped.length} ya activas${failed.length ? `, ${failed.length} fallaron` : ''}`;
      if (failed.length) toast.error(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar instancias');
    } finally {
      setServiceStatus(prev => ({ ...prev, [groupId]: null }));
    }
  };

  const handleStopService = async (groupId) => {
    setServiceStatus(prev => ({ ...prev, [groupId]: 'stopping' }));
    try {
      const resp = await prodigyGroupsAPI.stopAllBatches(groupId);
      const { stopped = [] } = resp.data || {};
      toast.success(`${stopped.length} instancias detenidas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al detener instancias');
    } finally {
      setServiceStatus(prev => ({ ...prev, [groupId]: null }));
    }
  };

  // ── Batch range ───────────────────────────────────────────────────────────

  const handleSaveRange = async (groupId) => {
    const range = editingRange[groupId];
    if (!range) return;
    const start = Number(range.start_batch);
    const end = range.end_batch !== '' ? Number(range.end_batch) : null;
    if (!start || start < 1) return;
    const group = groups.find(g => g.id === groupId);
    try {
      await prodigyGroupsAPI.updateBatch(groupId, {
        current_batch: group?.current_batch || start,
        start_batch: start,
        end_batch: end,
      });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, start_batch: start, end_batch: end } : g));
      setEditingRange(prev => { const n = { ...prev }; delete n[groupId]; return n; });
      toast.success('Rango actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar rango');
    }
  };

  // ── Delete group ──────────────────────────────────────────────────────────

  const handleDeleteGroup = async (groupId, name) => {
    if (!window.confirm(`¿Eliminar el grupo "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await prodigyGroupsAPI.deleteGroup(groupId);
      toast.success('Grupo eliminado');
      setGroups(prev => prev.filter(g => g.id !== groupId));
    } catch {
      toast.error('Error al eliminar grupo');
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────

  const handleToggleActive = async (group) => {
    try {
      const res = await prodigyGroupsAPI.updateGroup(group.id, { is_active: !group.is_active });
      setGroups(prev => prev.map(g => g.id === group.id ? res.data : g));
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  // ── Rename group ──────────────────────────────────────────────────────────

  const handleSaveName = async (groupId) => {
    const name = (editingName[groupId] || '').trim();
    if (!name) return;
    try {
      const res = await prodigyGroupsAPI.updateGroup(groupId, { name });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data : g));
      setEditingName(prev => { const n = { ...prev }; delete n[groupId]; return n; });
      toast.success('Nombre actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar nombre');
    }
  };

  // ── Batch ─────────────────────────────────────────────────────────────────

  const handleSaveBatch = async (groupId) => {
    const val = Number(editingBatch[groupId]);
    if (!val || val < 1) return;
    try {
      await prodigyGroupsAPI.updateBatch(groupId, val);
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, current_batch: val } : g));
      setEditingBatch(prev => { const n = { ...prev }; delete n[groupId]; return n; });
      toast.success('Batch actualizado');
    } catch {
      toast.error('Error al actualizar batch');
    }
  };

  // ── Members ───────────────────────────────────────────────────────────────

  const handleRemoveMember = async (groupId, userId) => {
    try {
      const res = await prodigyGroupsAPI.removeMember(groupId, userId);
      setGroups(prev => prev.map(g => g.id === groupId ? res.data : g));
    } catch {
      toast.error('Error al quitar miembro');
    }
  };

  const handleAddMember = async (groupId) => {
    const userId = addingMember[groupId];
    if (!userId) return;
    try {
      const res = await prodigyGroupsAPI.addMember(groupId, { user_id: Number(userId) });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data : g));
      setAddingMember(prev => { const n = { ...prev }; delete n[groupId]; return n; });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar miembro');
    }
  };

  // ── Config ────────────────────────────────────────────────────────────────

  const handleSaveConfig = async (key) => {
    const value = editingConfig[key];
    if (value === undefined || value === '') return;
    try {
      const res = await prodigyGroupsAPI.updateConfig(key, value);
      setConfig(prev => prev.map(c => c.key === key ? res.data : c));
      setEditingConfig(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Configuración guardada');
    } catch {
      toast.error('Error al guardar configuración');
    }
  };

  // ── Multi-select helper ───────────────────────────────────────────────────

  const toggleFormMember = (userId) => {
    setForm(prev => ({
      ...prev,
      member_user_ids: prev.member_user_ids.includes(userId)
        ? prev.member_user_ids.filter(id => id !== userId)
        : [...prev.member_user_ids, userId],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Administración Prodigy</h1>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'groups', label: 'Grupos de Anotadores' },
              { key: 'processes', label: `Procesos (${instances.length})` },
              { key: 'config', label: 'Configuración' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Tab: Grupos ─────────────────────────────────────────────────── */}
        {activeTab === 'groups' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">{groups.length} grupo(s)</p>
              <button
                onClick={openModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
              >
                + Nuevo Grupo
              </button>
            </div>

            <div className="space-y-4">
              {groups.map(group => {
                const memberIds = (group.members || []).map(m => m.user_id);
                const isEditingName = editingName[group.id] !== undefined;
                const availableToAdd = users.filter(u => !memberIds.includes(u.id));

                return (
                  <div key={group.id} className={`bg-white rounded-lg shadow-sm border ${group.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'} p-4`}>
                    <div className="flex flex-wrap items-start gap-3">
                      {/* Name */}
                      <div className="flex-1 min-w-48">
                        {isEditingName ? (
                          <div className="flex gap-2">
                            <input
                              className="border rounded px-2 py-1 text-sm flex-1"
                              value={editingName[group.id]}
                              onChange={e => setEditingName(prev => ({ ...prev, [group.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveName(group.id); if (e.key === 'Escape') setEditingName(prev => { const n = { ...prev }; delete n[group.id]; return n; }); }}
                              autoFocus
                            />
                            <button onClick={() => handleSaveName(group.id)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">OK</button>
                            <button onClick={() => setEditingName(prev => { const n = { ...prev }; delete n[group.id]; return n; })} className="text-xs text-gray-500 px-1">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{group.name}</span>
                            <button
                              onClick={() => setEditingName(prev => ({ ...prev, [group.id]: group.name }))}
                              className="text-gray-400 hover:text-gray-600 text-xs"
                              title="Renombrar"
                            >✎</button>
                          </div>
                        )}
                        <div className="mt-1"><TypeBadge type={group.annotation_type} /></div>
                      </div>

                      {/* Batch + Range */}
                      <div className="flex items-center gap-3 text-sm flex-wrap">
                        <span className="text-gray-500">Batch:</span>
                        {editingBatch[group.id] !== undefined ? (
                          <div className="flex gap-1">
                            <input
                              type="number"
                              min="1"
                              className="border rounded px-2 py-1 text-sm w-16"
                              value={editingBatch[group.id]}
                              onChange={e => setEditingBatch(prev => ({ ...prev, [group.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveBatch(group.id); if (e.key === 'Escape') setEditingBatch(prev => { const n = { ...prev }; delete n[group.id]; return n; }); }}
                              autoFocus
                            />
                            <button onClick={() => handleSaveBatch(group.id)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">OK</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingBatch(prev => ({ ...prev, [group.id]: group.current_batch || 1 }))}
                            className="font-mono font-semibold text-blue-700 hover:underline"
                            title="Editar batch actual"
                          >
                            #{group.current_batch ?? '—'}
                          </button>
                        )}

                        {/* Range display / edit */}
                        {editingRange[group.id] ? (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400 text-xs">Rango:</span>
                            <input
                              type="number" min="1"
                              className="border rounded px-1 py-0.5 text-xs w-14"
                              placeholder="desde"
                              value={editingRange[group.id].start_batch}
                              onChange={e => setEditingRange(prev => ({ ...prev, [group.id]: { ...prev[group.id], start_batch: e.target.value } }))}
                            />
                            <span className="text-gray-400">→</span>
                            <input
                              type="number" min="1"
                              className="border rounded px-1 py-0.5 text-xs w-14"
                              placeholder="hasta"
                              value={editingRange[group.id].end_batch}
                              onChange={e => setEditingRange(prev => ({ ...prev, [group.id]: { ...prev[group.id], end_batch: e.target.value } }))}
                            />
                            <button onClick={() => handleSaveRange(group.id)} className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">OK</button>
                            <button onClick={() => setEditingRange(prev => { const n = { ...prev }; delete n[group.id]; return n; })} className="text-xs text-gray-400">✕</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingRange(prev => ({ ...prev, [group.id]: { start_batch: group.start_batch || 1, end_batch: group.end_batch ?? '' } }))}
                            className="text-xs text-gray-400 hover:text-gray-600 font-mono"
                            title="Editar rango de batches"
                          >
                            {group.start_batch != null || group.end_batch != null
                              ? `(${group.start_batch ?? '?'}→${group.end_batch ?? '∞'})`
                              : '(sin rango)'}
                          </button>
                        )}
                      </div>

                      {/* Active toggle */}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Activo:</span>
                        <button
                          onClick={() => handleToggleActive(group)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${group.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${group.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                      </div>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteGroup(group.id, group.name)}
                        className="text-red-500 hover:text-red-700 text-sm ml-auto"
                        title="Eliminar grupo"
                      >
                        🗑
                      </button>

                      {/* Start / Stop service */}
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleStartService(group.id)}
                          disabled={serviceStatus[group.id] === 'starting' || serviceStatus[group.id] === 'stopping'}
                          className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
                          title="Forzar inicio de TODAS las instancias del rango (los anotadores normalmente inician la suya desde su panel)"
                        >
                          {serviceStatus[group.id] === 'starting' ? '⏳' : '▶'} Iniciar todos
                        </button>
                        <button
                          onClick={() => handleStopService(group.id)}
                          disabled={serviceStatus[group.id] === 'starting' || serviceStatus[group.id] === 'stopping'}
                          className="flex items-center gap-1 text-xs bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                          title="Detener todas las instancias del grupo"
                        >
                          {serviceStatus[group.id] === 'stopping' ? '⏳' : '⏹'} Detener todos
                        </button>
                      </div>
                    </div>

                    {/* Members */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-gray-500 font-medium">Miembros:</span>
                        {(group.members || []).map(m => (
                          <span key={m.user_id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                            {m.username}
                            <button
                              onClick={() => handleRemoveMember(group.id, m.user_id)}
                              className="text-gray-400 hover:text-red-500 leading-none"
                              title="Quitar"
                            >✕</button>
                          </span>
                        ))}
                        {availableToAdd.length > 0 && (
                          <div className="flex gap-1 items-center">
                            <select
                              value={addingMember[group.id] || ''}
                              onChange={e => setAddingMember(prev => ({ ...prev, [group.id]: e.target.value }))}
                              className="border rounded text-xs px-1 py-0.5 text-gray-700"
                            >
                              <option value="">+ agregar</option>
                              {availableToAdd.map(u => (
                                <option key={u.id} value={u.id}>{u.username}</option>
                              ))}
                            </select>
                            {addingMember[group.id] && (
                              <button
                                onClick={() => handleAddMember(group.id)}
                                className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded"
                              >Agregar</button>
                            )}
                          </div>
                        )}
                        {group.members?.length === 0 && !availableToAdd.length && (
                          <span className="text-xs text-gray-400 italic">Sin miembros</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {groups.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg">No hay grupos configurados</p>
                  <p className="text-sm mt-1">Creá el primer grupo con el botón de arriba</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Procesos ────────────────────────────────────────────────── */}
        {activeTab === 'processes' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {instances.length === 0 ? 'Sin procesos activos' : `${instances.length} proceso(s) corriendo`}
              </p>
              <button
                onClick={() => { setLoadingInstances(true); loadInstances().finally(() => setLoadingInstances(false)); }}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                {loadingInstances ? '⏳' : '↻'} Actualizar
              </button>
            </div>

            {instances.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-4xl mb-3">⬜</p>
                <p className="text-lg font-medium">No hay instancias Prodigy activas</p>
                <p className="text-sm mt-1">Iniciá instancias desde la pestaña Grupos</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Service ID</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Grupo</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Tipo</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Batch</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Puerto</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Dataset</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Anotadores</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Iniciado</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {instances.map(inst => {
                      const tc = TYPE_COLORS[inst.annotation_type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: inst.annotation_type };
                      const startedAt = inst.started_at ? new Date(inst.started_at) : null;
                      const uptime = startedAt ? (() => {
                        const secs = Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000));
                        if (secs < 60) return `${secs}s`;
                        if (secs < 3600) return `${Math.floor(secs/60)}m ${secs%60}s`;
                        return `${Math.floor(secs/3600)}h ${Math.floor((secs%3600)/60)}m`;
                      })() : '—';
                      return (
                        <tr key={inst.service_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs text-gray-800">{inst.service_id}</td>
                          <td className="px-4 py-3 text-gray-700">{inst.group_name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.text}`}>
                              {tc.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">#{inst.batch}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 font-mono text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                              :{inst.port}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600 max-w-[180px] truncate" title={inst.dataset}>{inst.dataset || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{(inst.members || []).join(', ') || '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{uptime}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleStopInstance(inst.service_id)}
                              disabled={stoppingInstance[inst.service_id]}
                              className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {stoppingInstance[inst.service_id] ? '...' : 'Detener'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Configuración ───────────────────────────────────────────── */}
        {activeTab === 'config' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.map(item => {
              const isEditing = editingConfig[item.key] !== undefined;
              return (
                <div key={item.key} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs text-gray-500 font-mono">{item.key}</p>
                      {item.description && <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>}
                    </div>
                  </div>
                  {isEditing ? (
                    <div className="flex gap-2 mt-2">
                      <input
                        className="border rounded px-2 py-1 text-sm flex-1 font-mono"
                        value={editingConfig[item.key]}
                        onChange={e => setEditingConfig(prev => ({ ...prev, [item.key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveConfig(item.key); if (e.key === 'Escape') setEditingConfig(prev => { const n = { ...prev }; delete n[item.key]; return n; }); }}
                        autoFocus
                      />
                      <button onClick={() => handleSaveConfig(item.key)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Guardar</button>
                      <button onClick={() => setEditingConfig(prev => { const n = { ...prev }; delete n[item.key]; return n; })} className="text-xs text-gray-500">✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-2xl font-bold text-gray-900 font-mono">{item.value}</span>
                      <button
                        onClick={() => setEditingConfig(prev => ({ ...prev, [item.key]: item.value }))}
                        className="text-xs text-blue-600 hover:underline"
                      >Editar</button>
                    </div>
                  )}
                </div>
              );
            })}
            {config.length === 0 && (
              <div className="col-span-3 text-center py-12 text-gray-400">Sin configuración</div>
            )}
          </div>
        )}
      </div>

      {/* ── Create Group Modal ───────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Nuevo Grupo Prodigy</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            <form onSubmit={handleCreateGroup} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ej. Grupo POS A"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de anotación</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.annotation_type}
                  onChange={e => setForm(prev => ({ ...prev, annotation_type: e.target.value }))}
                >
                  {Object.entries(TYPE_COLORS).map(([key, c]) => (
                    <option key={key} value={key}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch inicial</label>
                <input
                  type="number"
                  min="1"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.initial_batch}
                  onChange={e => setForm(prev => ({ ...prev, initial_batch: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch inicio rango</label>
                  <input
                    type="number" min="1"
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ej. 1"
                    value={form.start_batch}
                    onChange={e => setForm(prev => ({ ...prev, start_batch: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch fin rango</label>
                  <input
                    type="number" min="1"
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ej. 20 (vacío = sin límite)"
                    value={form.end_batch}
                    onChange={e => setForm(prev => ({ ...prev, end_batch: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Miembros</label>
                <div className="border rounded-md max-h-40 overflow-y-auto divide-y">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.member_user_ids.includes(u.id)}
                        onChange={() => toggleFormMember(u.id)}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">{u.username}</span>
                    </label>
                  ))}
                  {users.length === 0 && (
                    <p className="px-3 py-2 text-sm text-gray-400 italic">No hay usuarios activos</p>
                  )}
                </div>
                {form.member_user_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{form.member_user_ids.length} seleccionado(s)</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Creando...' : 'Crear Grupo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProdigyAdminPage;
